// src/App.js
import React, { useState, useEffect } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from './config/firebase.config';
import { supabase } from './config/supabase.config';
import { initEmailJS } from './config/emailjs.config';
import LoadingScreen from './components/LoadingScreen';

import Login from './pages/Login';
import Register from './pages/Register';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SuperadminDashboard from './pages/SuperadminDashboard';
import ForgotPassword from './pages/ForgotPassword';
import notificationService from './services/notification.service';
import styles from './App.module.css';

function App() {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [view, setView] = useState('login');
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    initEmailJS();
    const loadingTimer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 3000);
    return () => clearTimeout(loadingTimer);
  }, []);

// In useEffect after line 26, add:
useEffect(() => {
  // Initialize FCM when user logs in
  if (userData && userData.id) {
    initializeFCM();
  }
}, [userData]);

const initializeFCM = async () => {
  try {
    const result = await notificationService.initialize();
    if (result.success && result.token) {
      console.log('FCM initialized with token:', result.token);
      // Token is automatically saved by the service
    }
  } catch (error) {
    console.error('FCM initialization error:', error);
  }
};

  useEffect(() => {
    if (!isInitialLoading) {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          try {
            const { data, error } = await supabase
              .from('residents')
              .select('*')
              .eq('firebase_uid', firebaseUser.uid)
              .single();
            
            if (data && !error) {
              setUser(firebaseUser);
              setUserData(data);
              setView('dashboard');
              localStorage.setItem('userId', data.id);
              localStorage.setItem('userEmail', data.email);
              checkNotificationStatus();
            } else {
              console.error('Error fetching user data:', error);
              setUser(null);
              setUserData(null);
              setView('login');
            }
          } catch (error) {
            console.error('Error in auth state change:', error);
            setUser(null);
            setUserData(null);
            setView('login');
          }
        } else {
          setUser(null);
          setUserData(null);
          setView('login');
        }
      });
      
      return () => unsubscribe();
    }
  }, [isInitialLoading]);

  const checkNotificationStatus = () => {
    if ('Notification' in window) {
      const status = Notification.permission;
      setNotificationPermission(status);
      if (status === 'default') {
        setShowNotificationBanner(true);
      }
    }
  };

  const handleEnableNotifications = async () => {
    try {
      if (!('Notification' in window)) {
        alert('This browser does not support notifications');
        return;
      }

      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setNotificationPermission('granted');
        setShowNotificationBanner(false);
        
        new Notification('Notifications Enabled! 🔔', {
          body: 'You will now receive payment reminders and announcements.',
          icon: '/logo192.png'
        });
      } else if (permission === 'denied') {
        alert('Notifications blocked. Please enable them in your browser settings.');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      alert('Failed to enable notifications. Please check your browser settings.');
    }
  };

  const handleLoginSuccess = (firebaseUser, dbUserData) => {
    setIsLoggingOut(false);
    setUser(firebaseUser);
    setUserData(dbUserData);
    setView('dashboard');
    
    setTimeout(() => {
      checkNotificationStatus();
    }, 1000);
  };

  const handleLogout = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      setIsLoggingOut(true);
      setUser(null);
      setUserData(null);
      localStorage.clear();
      await signOut(auth);
      setView('login');
      
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 500);
    } catch (error) {
      console.error('Error during logout:', error);
      setIsLoggingOut(false);
      alert('Error logging out. Please try again.');
    }
  };

  const renderDashboard = () => {
    if (!userData) return <div>Loading...</div>;
    
    const dashboardProps = {
      user,
      userData,
      onLogout: handleLogout
    };

    switch (userData.role) {
      case 'superadmin':
        return <SuperadminDashboard {...dashboardProps} />;
      case 'admin':
        return <AdminDashboard {...dashboardProps} />;
      default:
        return <UserDashboard {...dashboardProps} />;
    }
  };

  if (isInitialLoading) {
    return <LoadingScreen onLoadComplete={() => setIsInitialLoading(false)} />;
  }

  return (
    <div className="App">
      {showNotificationBanner && view === 'dashboard' && notificationPermission !== 'granted' && (
        <div className="notification-banner" style={{
          position: 'fixed',
          top: 60,
          left: 0,
          right: 0,
          background: 'linear-gradient(90deg, #FFF3CD 0%, #FFE69C 100%)',
          padding: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          borderBottom: '2px solid #FFC107'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '15px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <span style={{ 
              fontSize: '14px', 
              color: '#856404',
              fontWeight: '500'
            }}>
              🔔 Enable notifications to receive important payment reminders and announcements
            </span>
            <button
              type="button"
              onClick={handleEnableNotifications}
              style={{
                padding: '6px 16px',
                background: '#28A745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Enable Notifications
            </button>
            <button
              type="button"
              onClick={() => setShowNotificationBanner(false)}
              style={{
                padding: '4px 8px',
                background: 'transparent',
                color: '#856404',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className={`app-container ${view === 'dashboard' ? 'dashboard-view' : ''}`}>
        {view === 'forgot-password' && (
          <ForgotPassword
            onBackToLogin={() => setView('login')}
          />
        )}
        
        {view === 'login' && !isLoggingOut && (
          <Login
            onLoginSuccess={handleLoginSuccess}
            onSwitchToRegister={() => setView('register')}
            onSwitchToForgotPassword={() => setView('forgot-password')}
          />
        )}
        
        {view === 'register' && (
          <Register
            onRegisterSuccess={() => setView('login')}
            onSwitchToLogin={() => setView('login')}
          />
        )}
        
        {view === 'dashboard' && userData && (
          <>
            <div className="app-header-wrapper">
              <div className="app-header" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '60px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '0 30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                zIndex: 1500,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h1 style={{ 
                  color: 'white', 
                  margin: 0, 
                  fontSize: '16.5px',
                  fontWeight: '600',
                  textAlign: 'center',
                  flex: 1
                }}>
                  Community Management System
                </h1>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {notificationPermission === 'granted' ? (
                    <button
                      type="button"
                      onClick={() => {
                        new Notification('Test Notification 🔔', {
                          body: 'Notifications are working!',
                          icon: '/logo192.png'
                        });
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '8px 12px',
                        background: 'rgba(76, 175, 80, 0.3)',
                        border: '1px solid rgba(76, 175, 80, 0.5)',
                        borderRadius: '5px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      <span>🔔</span>
                      <span>Active</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleEnableNotifications}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '8px 12px',
                        background: 'rgba(255, 193, 7, 0.3)',
                        border: '1px solid rgba(255, 193, 7, 0.5)',
                        borderRadius: '5px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      <span>🔕</span>
                      <span>Enable</span>
                    </button>
                  )}
                  
                  <button 
                    type="button"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    style={{
                      padding: '10px 20px',
                      background: isLoggingOut ? 'rgba(150, 150, 150, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '5px',
                      cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      opacity: isLoggingOut ? 0.6 : 1
                    }}
                  >
                    {isLoggingOut ? 'Logging out...' : 'Logout'}
                  </button>
                </div>
              </div>
            </div>
           {userData.role === 'admin' ? (
      <div style={{ paddingTop: '0', minHeight: 'calc(100vh - 60px)', background: '#f0f2f5', width: '100vw',  // Full viewport width
          margin: 0,  overflow: 'hidden'   }}>
        {renderDashboard()}
      </div>
    ) : (
      <div className="dashboard-container-with-header">
        {renderDashboard()}
      </div>
    )}  
          </>
        )}
        
        {isLoggingOut && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="spinner"></div>
              <p>Logging out...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;